import { useQuery } from '@tanstack/react-query';
import { getMyOpsUser, getOpsUsers } from '../../api/endpoints/ops';
import { staticQueryPolicy } from '../../api/queryPolicies';
import type { OpsUser, OpsUserSummary, ShipmentStatus } from '../../api/types';

export const opsUserKeys = {
  me: ['admin', 'ops-users', 'me'] as const,
  all: ['admin', 'ops-users'] as const,
};

export const ownershipEventsKey = (shipmentId: number) =>
  ['admin', 'shipments', 'ownership-events', shipmentId] as const;

/** Query keys every ownership mutation must refresh. */
export const OWNERSHIP_INVALIDATE_KEYS: unknown[][] = [
  ['admin', 'shipments'],
  ['admin', 'work'],
  ['admin', 'ops-users'],
];

export function isManager(user: Pick<OpsUser, 'role'> | null | undefined): boolean {
  return user?.role === 'OPS_MANAGER';
}

/**
 * The signed-in ops user (GET /ops-users/me) and their role. Manager-only
 * controls render only when `isManager` is true; the API enforces with 403.
 */
export function useOpsUser() {
  const query = useQuery({
    queryKey: opsUserKeys.me,
    queryFn: getMyOpsUser,
    ...staticQueryPolicy,
  });
  return {
    opsUser: query.data ?? null,
    role: query.data?.role ?? null,
    isManager: isManager(query.data),
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

/** All non-INACTIVE ops users (sorted by first name by the API). */
export function useOpsUsers(enabled = true) {
  return useQuery({
    queryKey: opsUserKeys.all,
    queryFn: getOpsUsers,
    enabled,
    ...staticQueryPolicy,
  });
}

export function opsUserName(user: Pick<OpsUserSummary, 'firstName' | 'lastName' | 'email'>): string {
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return name || user.email;
}

export function opsUserInitials(
  user: Pick<OpsUserSummary, 'firstName' | 'lastName' | 'email'>,
): string {
  const first = user.firstName?.trim()?.[0];
  const last = user.lastName?.trim()?.[0];
  if (first || last) return `${first ?? ''}${last ?? ''}`.toUpperCase();
  return user.email.slice(0, 2).toUpperCase();
}

/** Contract §1 work-phone rule, mirrored from the API. */
export function isValidWorkPhone(phone: string | null | undefined): boolean {
  if (!phone) return false;
  return /^\+91[6-9]\d{9}$/.test(phone.replace(/[\s-]/g, ''));
}

/** "Open order" = status not in COMPLETED / CANCELLED / EXPIRED. */
export function isOpenShipment(status: ShipmentStatus): boolean {
  return status !== 'COMPLETED' && status !== 'CANCELLED' && status !== 'EXPIRED';
}

export const REASON_MIN = 3;
export const REASON_MAX = 500;

export function validateReason(reason: string): string | null {
  const length = reason.trim().length;
  if (length < REASON_MIN) return `Reason must be at least ${REASON_MIN} characters.`;
  if (length > REASON_MAX) return `Reason must be ${REASON_MAX} characters or fewer.`;
  return null;
}
